import { EventProperties } from '@segment/analytics-next'
import { useEffect, useState } from 'react'
import useFeatureToggle from '@/hooks/useFeatureToggle'
import { shouldActivateAnalytics } from '@/contexts/analytics'
import { FeatureToggle } from '@/resources/enums/featureToggle'
import { useRouterStore } from '@/contexts/router'
import { client, sendAnalyticsEvent } from '@/analytics/segmentClient'
import { useSellerAnalyticsFields } from '@/hooks/useAnalyticsSellerContext'

type AnalyticsTrackFn = (
  eventName: string,
  properties?: EventProperties,
  forceEvent?: boolean
) => Promise<boolean>

type UseAnalytics = () => AnalyticsTrackFn

export const useAnalytics: UseAnalytics = () => {
  const analyticsEnableToggle = useFeatureToggle({
    splitName: 'enable_segment_analytics',
    includeLoadingState: true,
  })

  const [isAnalyticsEnabled, setIsAnalyticsEnabled] = useState(false)
  const getSellerAnalyticsContext = useSellerAnalyticsFields()

  const {
    previousPage: previous_screen_name = null,
    currentPage: screen_name = null,
  } = useRouterStore()

  useEffect(() => {
    setIsAnalyticsEnabled(analyticsEnableToggle === FeatureToggle.ON)
    if (analyticsEnableToggle === FeatureToggle.OFF) {
      console.error(
        'Analytics is disabled! Check the feature toggle "enable_segment_analytics"'
      )
    }
  }, [analyticsEnableToggle])

  useEffect(() => {
    if (!client && shouldActivateAnalytics) {
      console.error(
        'Context used outside of its Provider or Segment writeKey not provided!'
      )
    }
  }, [])

  const trackEvent: AnalyticsTrackFn = async (
    eventName,
    eventProperties = {},
    forceEvent = false
  ) => {
    if (isAnalyticsEnabled || forceEvent) {
      const context = getSellerAnalyticsContext(eventProperties)
      if (!context) return false

      await sendAnalyticsEvent(eventName, {
        screen_name,
        previous_screen_name,
        ...context,
      })
      return true
    }

    return false
  }

  return trackEvent
}
